// OPERATOR-ONLY-UX: an agency operator in a client company where it holds no person membership
// (an operator-managed company with no owner). The API refuses its Chat and Work writes and the
// `my/*` reads (they belong to a person member: `domain/chat/principal.ts` `authorOf`,
// `domain/me/objects.ts`), so the app fires none of them and says why once, inline, with the way
// forward: invite a person from Setup › People. The rules themselves are unchanged.
import {
  Alert,
  AlertDescription,
  AlertTitle,
  cn,
  DropdownMenuItem,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { UserRoundIcon } from "lucide-react";
import { cloneElement, type MouseEvent, type ReactElement, type ReactNode, useId } from "react";
import type { Api } from "../api/client";
import { meQuery } from "../api/query";
import { keepFocus } from "../focus/focus";
import { operatorOnlyIn } from "./company";

/** The tooltip of a create control an operator-only principal cannot use. */
export const OPERATOR_ONLY_REASON =
  "Chat, new spaces and personal task lists need a person member. Invite people from Setup › People.";

/** The short form a menu item shows beside its label. */
export const OPERATOR_ONLY_SHORT = "Needs a person member";

/** Whether the caller is operator-only in company `co` (false until `/v1/me` is known). */
export function useOperatorOnly(api: Api, co: string): boolean {
  const q = useQuery({ ...meQuery(api), select: (me) => operatorOnlyIn(me, co) });
  return q.data ?? false;
}

/** The one calm notice: why Work and Chat are read-only here, linked to People. */
export function OperatorOnlyNotice({
  slug,
  compact = false,
  className,
}: {
  slug: string;
  compact?: boolean;
  className?: string;
}) {
  const people = (
    <Link to="/c/$slug/setup/people" params={{ slug }} search={keepFocus as never}>
      Setup › People
    </Link>
  );
  if (compact)
    return (
      // A span (block): it also sits inside an empty state's description paragraph.
      <span
        data-slot="operator-only-notice"
        className={cn(
          "block [&_a]:underline",
          className ?? "px-4 py-1 text-xs text-muted-foreground",
        )}
      >
        You're here as your agency's operator. Chat, new spaces and personal task lists need a
        person member: invite people from {people}.
      </span>
    );
  return (
    <Alert data-slot="operator-only-notice" className={className}>
      <UserRoundIcon aria-hidden="true" />
      <AlertTitle>You're here as your agency's operator</AlertTitle>
      <AlertDescription className="[&_a]:underline">
        Chat, new spaces and personal task lists need a person member: invite people from {people}.
        Boards and tasks in existing spaces stay yours to manage. A member who holds the approver
        seat decides approvals.
      </AlertDescription>
    </Alert>
  );
}

/**
 * A menu item an operator-only principal cannot use: shown, disabled, with the short reason
 * visible beside its label (a disabled menu item takes no hover or focus, so no tooltip) and the
 * full reason for screen readers.
 */
export function OperatorOnlyMenuItem({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <DropdownMenuItem disabled data-slot="operator-only-item">
      {icon}
      {label}
      <span aria-hidden="true" className="ml-auto pl-3 text-xs text-muted-foreground">
        {OPERATOR_ONLY_SHORT}
      </span>
      <span className="sr-only">. {OPERATOR_ONLY_REASON}</span>
    </DropdownMenuItem>
  );
}

/** A disabled control with the operator-only reason as its tooltip (focusable, announced). */
export function OperatorOnlyDisabled({ children }: { children: ReactElement }) {
  const id = useId();
  const child = children as ReactElement<{
    className?: string;
    disabled?: boolean;
    onClick?: (e: MouseEvent) => void;
  }>;
  // `aria-disabled`, not `disabled`: the control keeps keyboard focus, so its tooltip opens from
  // the keyboard too, and screen readers announce it as unavailable with the reason.
  const control = cloneElement(child, {
    disabled: false,
    "aria-disabled": true,
    "aria-describedby": id,
    onClick: (e: MouseEvent) => e.preventDefault(),
    className: cn(child.props.className, "cursor-not-allowed opacity-50"),
  } as Record<string, unknown>);
  return (
    <span className="inline-flex" data-slot="operator-only-disabled">
      <Tooltip>
        <TooltipTrigger render={control} />
        <TooltipContent>{OPERATOR_ONLY_REASON}</TooltipContent>
      </Tooltip>
      <span id={id} className="sr-only">
        {OPERATOR_ONLY_REASON}
      </span>
    </span>
  );
}
